import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, MessageSquare, Send, Clock } from 'lucide-react';

type ContactForm = {
  fullName: string;
  email: string;
  subject: string;
  message: string;
};

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState<ContactForm>({
    fullName: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    toast.success(
      t('public.contact.success', {
        defaultValue: 'Thanks for reaching out! We received your message.',
      })
    );

    setForm({
      fullName: '',
      email: '',
      subject: '',
      message: '',
    });
  };

  return (
    <div className="flex flex-col">
      <section className="relative bg-gradient-to-b from-primary/10 via-background to-background py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
              {t('public.contact.title', { defaultValue: 'Contact Us' })}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('public.contact.subtitle', {
                defaultValue: 'Have a question or need help? Send us a message.',
              })}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto grid gap-8 lg:grid-cols-2">
            <Card className="border-border/50">
              <CardContent className="p-6 sm:p-8">
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <Label htmlFor="fullName">
                      {t('public.contact.fullName', { defaultValue: 'Full Name' })}
                    </Label>
                    <Input
                      id="fullName"
                      required
                      value={form.fullName}
                      onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t('public.contact.email', { defaultValue: 'Email Address' })}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">
                      {t('public.contact.subject', { defaultValue: 'Subject' })}
                    </Label>
                    <Input
                      id="subject"
                      required
                      value={form.subject}
                      onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">
                      {t('public.contact.message', { defaultValue: 'Message' })}
                    </Label>
                    <Textarea
                      id="message"
                      rows={5}
                      required
                      value={form.message}
                      onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                    />
                  </div>

                  <Button type="submit" className="w-full sm:w-auto">
                    {t('public.contact.send', { defaultValue: 'Send Message' })}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card className="border-border/50">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {t('public.contact.emailLabel', { defaultValue: 'Email' })}
                      </h3>
                      <p className="text-muted-foreground text-sm">support@tickally.com</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                      <Clock className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {t('public.contact.responseTime', { defaultValue: 'Response Time' })}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t('public.contact.responseTimeValue', {
                          defaultValue: 'We typically respond within 24 hours.',
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {t('public.contact.community', { defaultValue: 'Community' })}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t('public.contact.communityLinks', {
                          defaultValue: 'Follow us on social platforms for updates.',
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
